import React, { Fragment, useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Row, Col, Card, Button, Form, Spinner, Table, Alert } from "react-bootstrap";
import Select from "react-select";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import swal from "sweetalert";
import SignatureModal from "../Common/SignatureModal";

const ContractorDeliveryAdd = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  const projectFromUrl = searchParams.get("project") || "";
  const workOrderFromUrl = searchParams.get("workOrder") || "";
  const cwoFromUrl = searchParams.get("cwo") || "";

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [projects, setProjects] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [cwos, setCwos] = useState([]);
  const [vendors, setVendors] = useState([]);

  const [showSignModal, setShowSignModal] = useState(false);

  const [form, setForm] = useState({
    project: projectFromUrl,
    workOrder: workOrderFromUrl,
    contractualWorkOrder: cwoFromUrl,
    contractor: "",
    challanNumber: "",
    deliveryDate: new Date().toISOString().split("T")[0],
    status: "Pending",
    remarks: "",
    dispatchDetails: {
      driverName: "",
      driverPhone: "",
      vehicleNumber: "",
      transporterName: "",
    },
    items: [],
    signature: null,
  });

  // 1. Initial Load: Projects, Vendors, Work Orders
  useEffect(() => {
    const init = async () => {
      setFetching(true);
      try {
        const [projRes, woRes, vendorRes] = await Promise.all([
          api.get("/projects?limit=all"),
          api.get("/work-orders?limit=all"),
          api.get("/vendors?limit=all"),
        ]);
        setProjects(projRes.data?.data || []);
        setWorkOrders(woRes.data?.data || []);
        setVendors(vendorRes.data?.data || []);
      } catch (err) {
        console.error("Failed to load initial data", err);
      } finally {
        setFetching(false);
      }
    };
    init();
  }, []);

  // 2. Fetch CWOs when Work Order or Project changes
  useEffect(() => {
    const fetchCwos = async () => {
      if (!form.workOrder && !form.project) {
        setCwos([]);
        return;
      }
      try {
        const params = new URLSearchParams();
        if (form.workOrder) params.append("workOrder", form.workOrder);
        else if (form.project) params.append("project", form.project);
        params.append("limit", "100");

        const res = await api.get(`/contractual-work-orders?${params.toString()}`);
        setCwos(res.data?.data || []);
      } catch (err) {
        console.error("Failed to fetch CWOs", err);
      }
    };
    fetchCwos();
  }, [form.workOrder, form.project]);

  // 3. Edit Mode: load existing delivery
  useEffect(() => {
    if (!id) return;
    const fetchDelivery = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/contractor-delivery/${id}`);
        const d = res.data?.data;
        if (d) {
          setForm({
            project: d.project?._id || d.project || "",
            workOrder: d.workOrder?._id || d.workOrder || "",
            contractualWorkOrder: d.contractualWorkOrder?._id || d.contractualWorkOrder || "",
            contractor: d.contractor?._id || d.contractor || "",
            challanNumber: d.challanNumber || d.deliveryNumber || "",
            deliveryDate: d.deliveryDate ? d.deliveryDate.split("T")[0] : new Date().toISOString().split("T")[0],
            status: d.status || "Pending",
            remarks: d.remarks || "",
            dispatchDetails: {
              driverName: d.dispatchDetails?.driverName || "",
              driverPhone: d.dispatchDetails?.driverPhone || "",
              vehicleNumber: d.dispatchDetails?.vehicleNumber || "",
              transporterName: d.dispatchDetails?.transporterName || "",
            },
            items: (d.items || []).map((it) => ({
              ...it,
              quantityOrdered: it.quantityOrdered || 0,
              quantityDelivered: it.quantityDelivered !== undefined ? it.quantityDelivered : it.quantityOrdered || 0,
              rate: it.rate || 0,
              amount: it.amount || (it.quantityDelivered * it.rate),
            })),
            signature: d.signature || null,
          });
        }
      } catch (err) {
        swal("Error", "Failed to load Contractor Delivery", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchDelivery();
  }, [id]);

  // 4. Handle CWO Selection -> Auto-load ALL items from CWO
  const handleCwoChange = async (cwoId) => {
    if (!cwoId) {
      setForm((prev) => ({
        ...prev,
        contractualWorkOrder: "",
        contractor: "",
        items: [],
      }));
      return;
    }

    try {
      setLoading(true);
      const res = await api.get(`/contractual-work-orders/${cwoId}`);
      const cwoData = res.data?.data;

      if (!cwoData) return;

      const vendorId = cwoData.vendor?._id || cwoData.vendor || "";
      const woId = cwoData.workOrder?._id || cwoData.workOrder || form.workOrder;
      const projId = cwoData.workOrder?.project?._id || cwoData.workOrder?.project || form.project;

      // Map ALL items from CWO (both BOQ items & manual items)
      const cwoItems = (cwoData.selectedBoqItems || []).map((item) => {
        const qOrd = Number(item.quantity || 0);
        const rate = Number(item.price || item.rate || 0);
        return {
          boqItemId: item.boqItemId || "",
          isManual: !!item.isManual,
          wlino: item.wlino || "",
          itemName: item.itemName || "",
          description: item.description || "",
          unit: item.unit || "Nos",
          quantityOrdered: qOrd,
          quantityDelivered: qOrd, // default to ordered qty
          rate: rate,
          amount: qOrd * rate,
          remark: "",
        };
      });

      setForm((prev) => ({
        ...prev,
        contractualWorkOrder: cwoId,
        contractor: vendorId,
        workOrder: woId,
        project: projId,
        items: cwoItems,
      }));
    } catch (err) {
      swal("Error", "Failed to load CWO items", "error");
    } finally {
      setLoading(false);
    }
  };

  // 5. Handle Item Change in table
  const handleItemChange = (index, field, value) => {
    const updated = [...form.items];
    const isNum = field === "quantityDelivered" || field === "rate";
    updated[index] = {
      ...updated[index],
      [field]: isNum ? (value === "" ? "" : Number(value)) : value,
    };
    if (isNum) {
      const q = Number(updated[index].quantityDelivered || 0);
      const r = Number(updated[index].rate || 0);
      updated[index].amount = Number((q * r).toFixed(2));
    }
    setForm((prev) => ({ ...prev, items: updated }));
  };

  const removeItem = (index) => {
    const updated = form.items.filter((_, i) => i !== index);
    setForm((prev) => ({ ...prev, items: updated }));
  };

  const submitPayload = async (signatureData, overrideStatus) => {
    try {
      setSubmitting(true);
      const payload = {
        ...form,
        status: overrideStatus || form.status || (id ? undefined : "Pending"),
        signature: signatureData !== undefined ? signatureData : form.signature,
        items: form.items.map((i) => ({
          ...i,
          quantityDelivered: Number(i.quantityDelivered || 0),
          rate: Number(i.rate || 0),
          amount: Number(i.amount || 0),
        })),
      };

      if (id) {
        await api.patch(`/contractor-delivery/${id}`, payload);
        swal("Success", "Contractor Delivery Challan updated successfully", "success");
      } else {
        await api.post("/contractor-delivery", payload);
        swal("Success", "Contractor Delivery Challan created successfully", "success");
      }

      navigate(
        form.workOrder
          ? `/contractor-delivery?project=${form.project}&workOrder=${form.workOrder}`
          : "/contractor-delivery"
      );
    } catch (err) {
      console.error("Save Contractor Delivery error:", err);
      swal("Error", err.response?.data?.message || "Failed to save Contractor Delivery Challan", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // 6. Handle Submit (When editing: no signature needed; When creating: prompt signature)
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.project) return swal("Validation", "Please select a Project", "warning");
    if (!form.workOrder) return swal("Validation", "Please select a Work Order", "warning");
    if (!form.contractualWorkOrder) return swal("Validation", "Please select a Contractual Work Order (CWO)", "warning");
    if (!form.contractor) return swal("Validation", "Contractor is required", "warning");
    if (!form.items.length) return swal("Validation", "At least one item is required in the Delivery Challan", "warning");

    const invalidQty = form.items.some((i) => i.quantityDelivered === "" || Number(i.quantityDelivered) < 0);
    if (invalidQty) {
      return swal("Validation", "Please enter a valid delivered quantity for all items", "warning");
    }

    if (id) {
      // Update directly without asking for signature
      submitPayload(form.signature);
    } else {
      // Open Signature Modal only on creation
      setShowSignModal(true);
    }
  };

  const handleSaveSignature = async (signatureFile) => {
    setShowSignModal(false);
    try {
      setSubmitting(true);
      let signatureData = form.signature || null;

      if (signatureFile && signatureFile instanceof File) {
        const formData = new FormData();
        formData.append("file", signatureFile);

        const uploadRes = await api.post("/deliveries/upload/single", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (uploadRes.data?.success) {
          signatureData = {
            fileName: uploadRes.data.fileName,
            fileUrl: uploadRes.data.fileUrl,
            filePath: uploadRes.data.filePath,
            signedAt: new Date(),
          };
        }
      }

      await submitPayload(signatureData, "Pending");
    } catch (err) {
      console.error("Save signature error:", err);
      swal("Error", "Failed to upload signature", "error");
      setSubmitting(false);
    }
  };

  const handleSaveAsDraft = (e) => {
    e.preventDefault();
    if (!form.project) return swal("Validation", "Please select a Project", "warning");
    if (!form.workOrder) return swal("Validation", "Please select a Work Order", "warning");
    if (!form.contractualWorkOrder) return swal("Validation", "Please select a Contractual Work Order (CWO)", "warning");
    if (!form.contractor) return swal("Validation", "Contractor is required", "warning");
    if (!form.items.length) return swal("Validation", "At least one item is required in the Delivery Challan", "warning");

    submitPayload(form.signature, "Draft");
  };

  // Filtered lists for Selects
  const projectOptions = projects.map((p) => ({ value: p._id, label: `${p.projectName} (${p.code || "PRJ"})` }));
  const woOptions = workOrders
    .filter((w) => !form.project || String(w.project?._id || w.project) === String(form.project))
    .map((w) => ({ value: w._id, label: `${w.woNo || w.woNumber}${w.label ? ` – ${w.label}` : ""}` }));
  const cwoOptions = cwos.map((c) => ({
    value: c._id,
    label: `${c.cwoNo} — ${c.vendor?.name || "Contractor"} (₹${Number(c.totalAmount || c.value || 0).toLocaleString("en-IN")})`,
  }));

  const totalDeliveredAmount = useMemo(() => {
    return (form.items || []).reduce((sum, it) => sum + (Number(it.amount) || 0), 0);
  }, [form.items]);

  return (
    <Fragment>
      <PageTitle
        activeMenu={id ? "Edit Contractor DC" : "Create Contractor DC"}
        motherMenu="Material Management"
      />

      <div className="row">
        <Col lg={12}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <Card.Title>
                <i className="fas fa-truck-moving me-2 text-primary"></i>
                {id ? "Edit Contractor Delivery Challan" : "New Contractor Delivery Challan (DC)"}
              </Card.Title>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => navigate("/contractor-delivery")}
              >
                <i className="fas fa-arrow-left me-1"></i> Back to List
              </Button>
            </Card.Header>

            <Card.Body>
              {fetching ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2 text-muted">Loading details...</p>
                </div>
              ) : (
                <Form onSubmit={handleSubmit}>
                  {/* BASIC SECTION */}
                  <div className="mb-4">
                    <h6 className="fw-bold mb-3 text-primary">
                      <i className="fas fa-info-circle me-2"></i>Challan & Work Order Details
                    </h6>

                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Project *</Form.Label>
                          <Select
                            options={projectOptions}
                            value={projectOptions.find((p) => p.value === form.project) || null}
                            onChange={(opt) => {
                              setForm((prev) => ({
                                ...prev,
                                project: opt?.value || "",
                                workOrder: "",
                                contractualWorkOrder: "",
                                contractor: "",
                                items: [],
                              }));
                            }}
                            placeholder="Select Project..."
                            isClearable
                          />
                        </Form.Group>
                      </Col>

                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Work Order *</Form.Label>
                          <Select
                            options={woOptions}
                            value={woOptions.find((w) => w.value === form.workOrder) || null}
                            onChange={(opt) => {
                              const foundWo = workOrders.find((w) => w._id === opt?.value);
                              setForm((prev) => ({
                                ...prev,
                                workOrder: opt?.value || "",
                                project: foundWo?.project?._id || foundWo?.project || prev.project,
                                contractualWorkOrder: "",
                                contractor: "",
                                items: [],
                              }));
                            }}
                            placeholder="Select Work Order..."
                            isDisabled={!form.project && woOptions.length === 0}
                            isClearable
                          />
                        </Form.Group>
                      </Col>

                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-bold text-success">
                            Contractual Work Order (CWO) *
                          </Form.Label>
                          <Select
                            options={cwoOptions}
                            value={cwoOptions.find((c) => c.value === form.contractualWorkOrder) || null}
                            onChange={(opt) => handleCwoChange(opt?.value || "")}
                            placeholder="Select CWO to load items..."
                            isClearable
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Contractor / Vendor</Form.Label>
                          <Form.Control
                            value={vendors.find((v) => v._id === form.contractor)?.name || "Auto-loaded from CWO"}
                            disabled
                            className="bg-light"
                          />
                        </Form.Group>
                      </Col>

                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Challan Number (Manual / Ref)</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="e.g. DC-001 (Leave blank for auto-generated)"
                            value={form.challanNumber}
                            onChange={(e) => setForm({ ...form, challanNumber: e.target.value })}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Delivery Date *</Form.Label>
                          <Form.Control
                            type="date"
                            value={form.deliveryDate}
                            onChange={(e) => setForm({ ...form, deliveryDate: e.target.value })}
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>

                  {/* DISPATCH DETAILS */}
                  <div className="mb-4">
                    <h6 className="fw-bold mb-3 text-primary">
                      <i className="fas fa-shipping-fast me-2"></i>Dispatch & Vehicle Details
                    </h6>
                    <Row>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>Vehicle Number</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="e.g. MH-12-AB-1234"
                            value={form.dispatchDetails.vehicleNumber}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                dispatchDetails: { ...form.dispatchDetails, vehicleNumber: e.target.value },
                              })
                            }
                          />
                        </Form.Group>
                      </Col>

                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>Transporter Name</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="e.g. VRL Logistics"
                            value={form.dispatchDetails.transporterName}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                dispatchDetails: { ...form.dispatchDetails, transporterName: e.target.value },
                              })
                            }
                          />
                        </Form.Group>
                      </Col>

                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>Driver Name</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Driver Name"
                            value={form.dispatchDetails.driverName}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                dispatchDetails: { ...form.dispatchDetails, driverName: e.target.value },
                              })
                            }
                          />
                        </Form.Group>
                      </Col>

                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>Driver Phone</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Phone Number"
                            value={form.dispatchDetails.driverPhone}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                dispatchDetails: { ...form.dispatchDetails, driverPhone: e.target.value },
                              })
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>

                  {/* ITEMS SECTION */}
                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="fw-bold text-primary mb-0">
                        <i className="fas fa-boxes me-2"></i>Delivery Items ({form.items.length})
                      </h6>
                      {form.contractualWorkOrder && (
                        <span className="badge bg-info text-white">
                          Loaded from CWO (BOQ & Manual items included)
                        </span>
                      )}
                    </div>

                    {loading ? (
                      <div className="text-center py-4">
                        <Spinner animation="border" size="sm" variant="primary" />
                        <span className="ms-2">Loading items...</span>
                      </div>
                    ) : form.items.length === 0 ? (
                      <div
                        className="text-center py-4 px-3 rounded mb-3 shadow-sm"
                        style={{
                          backgroundColor: "#fff3cd",
                          border: "1px solid #ffeeba",
                          color: "#856404",
                          fontSize: "15px",
                        }}
                      >
                        <i className="fas fa-exclamation-triangle me-2 text-warning fs-5 align-middle"></i>
                        Please select a <strong style={{ color: "#533f03" }}>Contractual Work Order (CWO)</strong> above to load items into this Delivery Challan.
                      </div>
                    ) : (
                      <div className="table-responsive border rounded bg-white">
                        <Table hover striped className="mb-0">
                          <thead className="bg-light">
                            <tr>
                              <th style={{ width: "4%" }} className="text-center">#</th>
                              <th style={{ width: "10%" }}>WLINO</th>
                              <th>Item Name & Description</th>
                              <th style={{ width: "8%" }} className="text-center">Unit</th>
                              <th style={{ width: "12%" }} className="text-center">Ordered Qty</th>
                              <th style={{ width: "14%" }} className="text-center">Delivered Qty *</th>
                              <th style={{ width: "12%" }} className="text-end">Rate (₹)</th>
                              <th style={{ width: "12%" }} className="text-end">Amount (₹)</th>
                              <th style={{ width: "5%" }} className="text-center">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {form.items.map((item, index) => (
                              <tr key={item.boqItemId || index}>
                                <td className="text-center align-middle text-muted small">{index + 1}</td>
                                <td className="align-middle">
                                  {item.isManual ? (
                                    <span className="badge bg-secondary">Manual</span>
                                  ) : (
                                    <span className="fw-bold">{item.wlino || "-"}</span>
                                  )}
                                </td>
                                <td className="align-middle">
                                  <div className="fw-bold text-dark">{item.itemName}</div>
                                  {item.description && (
                                    <small className="text-muted d-block">{item.description}</small>
                                  )}
                                  <Form.Control
                                    size="sm"
                                    placeholder="Remarks / Location notes..."
                                    value={item.remark || ""}
                                    onChange={(e) => handleItemChange(index, "remark", e.target.value)}
                                    className="mt-1 small"
                                  />
                                </td>
                                <td className="text-center align-middle">
                                  <span className="badge bg-light text-dark border">{item.unit || "Nos"}</span>
                                </td>
                                <td className="text-center align-middle fw-bold text-muted">
                                  {item.quantityOrdered}
                                </td>
                                <td className="align-middle">
                                  <Form.Control
                                    type="number"
                                    size="sm"
                                    value={item.quantityDelivered}
                                    onChange={(e) => handleItemChange(index, "quantityDelivered", e.target.value)}
                                    className="text-center fw-bold text-primary"
                                    min="0"
                                    step="any"
                                    required
                                  />
                                </td>
                                <td className="text-end align-middle">
                                  ₹{Number(item.rate || 0).toFixed(2)}
                                </td>
                                <td className="text-end align-middle fw-bold text-success">
                                  ₹{Number(item.amount || 0).toFixed(2)}
                                </td>
                                <td className="text-center align-middle">
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    className="btn-icon rounded-circle border-0"
                                    onClick={() => removeItem(index)}
                                    title="Remove item"
                                  >
                                    <i className="fas fa-trash-alt"></i>
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="bg-light fw-bold">
                              <td colSpan="7" className="text-end py-3">Total Delivered Value:</td>
                              <td className="text-end py-3 text-success fs-6">
                                ₹{totalDeliveredAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                              </td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </Table>
                      </div>
                    )}
                  </div>

                  {/* REMARKS */}
                  <Row className="mb-4">
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Challan Remarks</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          placeholder="General delivery remarks, terms, or conditions..."
                          value={form.remarks}
                          onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* SUBMIT BUTTONS */}
                  <div className="d-flex justify-content-end gap-2 border-top pt-3">
                    <Button
                      variant="secondary"
                      onClick={() => navigate("/contractor-delivery")}
                      disabled={submitting}
                    >
                      Cancel
                    </Button>
                    {!id && (
                      <Button
                        variant="outline-secondary"
                        type="button"
                        onClick={handleSaveAsDraft}
                        disabled={submitting || form.items.length === 0}
                      >
                        Save as Draft
                      </Button>
                    )}
                    <Button variant="primary" type="submit" disabled={submitting || form.items.length === 0}>
                      {submitting ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-1" />
                          Saving...
                        </>
                      ) : id ? (
                        "Update Contractor DC"
                      ) : (
                        "Create Contractor DC"
                      )}
                    </Button>
                  </div>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>
      </div>

      {/* Signature Modal at Creation */}
      <SignatureModal
        show={showSignModal}
        onHide={() => {
          setShowSignModal(false);
          setSubmitting(false);
        }}
        onSave={handleSaveSignature}
        title="Authorised Signatory Signature for Contractor Delivery Challan"
      />
    </Fragment>
  );
};

export default ContractorDeliveryAdd;

